import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import EmailOptIn from './EmailOptIn';

const Hero: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let particles: Particle[] = [];
    let animationFrameId: number;
    let scrollPosition = 0;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      originalX: number;
      originalY: number;
      
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.originalX = this.x;
        this.originalY = this.y;
        this.size = Math.random() * 1.5 + 0.5;
        this.speedX = Math.random() * 2 - 1;
        this.speedY = Math.random() * 2 - 1;
        this.color = '#C7A16B';
      }
      
      update(scrollFactor: number) {
        if (scrollFactor < 1) {
          this.x += this.speedX;
          this.y += this.speedY;
          
          if (this.x > canvas.width) this.x = 0;
          else if (this.x < 0) this.x = canvas.width;
          
          if (this.y > canvas.height) this.y = 0;
          else if (this.y < 0) this.y = canvas.height;
        } else {
          // Move towards original position
          this.x = this.x + (this.originalX - this.x) * 0.1;
          this.y = this.y + (this.originalY - this.y) * 0.1;
        }
      }
      
      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    const init = () => {
      particles = [];
      const particleCount = Math.min(50, Math.floor(window.innerWidth * 0.03));
      
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };
    
    const handleScroll = () => {
      scrollPosition = window.scrollY;
    };
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const scrollFactor = Math.min(scrollPosition / 500, 1);
      
      for (let i = 0; i < particles.length; i++) {
        particles[i].update(scrollFactor);
        particles[i].draw();
      }
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(199, 161, 107, ${0.3 - (distance/100) * 0.3})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      init();
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    init();
    animate();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-dark">
      <header className="w-full py-6 relative z-30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <svg width="203" height="37" viewBox="0 0 203 37" className="mx-auto h-8 w-auto">
              <g fill="#FFFFFF" fillRule="nonzero">
                <path d="M19.5,3 C25.7,3 30.8,3.5 30.8,10.2 C30.8,14 28.1,17 21.5,17.5 L21.5,18 C26.8,18.4 31.9,19.4 31.9,26.1 C31.9,34.3 23.3,35.6 16.9,35.6 L0,35.6 L0,34 C2.8,33.9 3.1,33.6 3.1,31.1 L3.1,7.5 C3.1,5 2.8,4.7 0,4.6 L0,3 L19.5,3 Z M14.2,17.3 L15.5,17.3 C18.8,17.3 19.7,15.7 19.7,11.3 C19.7,6.6 17.7,4.9 16,4.9 C14.2,4.9 14.2,5.3 14.2,6.2 L14.2,17.3 Z M14.2,31.2 C14.2,32.8 14.3,33.7 16.4,33.7 C18.7,33.7 20.4,31.2 20.4,26.4 C20.4,21.9 19,19.2 15.5,19.2 L14.2,19.2 L14.2,31.2 L14.2,31.2 Z" />
                <path d="M44.1,22.8 C44.1,28.1 45.9,31.9 50,31.9 C53.4,31.9 55.2,31.3 56.8,27.8 L58.1,28.5 C56.2,34.6 51.6,36.2 46.3,36.2 C37.4,36.2 33.1,32.4 33.1,23 C33.1,14.2 37.9,9.5 46.5,9.5 C58.5,9.5 58.6,17.6 58.6,20.2 C58.6,21.9 58.1,22.8 56.4,22.8 L44.1,22.8 Z M47.1,20.6 C48.4,20.5 48.8,20.3 48.8,18.3 C48.8,16.6 48.8,11.3 46.5,11.3 C44.5,11.3 44,16.4 44,20.7 L47.1,20.6 Z" />
                <path d="M59.1,25.8 L60.8,25.8 C62.4,29.8 65.8,34.4 69.3,34.4 C71,34.4 72.3,33.7 72.3,31.5 C72.3,29.5 70.5,28.3 67,26.8 C62.9,25.1 59.5,23.3 59.5,17.9 C59.5,12.5 63.5,9.5 70.5,9.5 C73.2,9.5 76.4,10.1 78.8,11.3 L78.8,18.9 L77.1,18.9 C75.5,14.7 73,11.5 70.4,11.5 C68.7,11.5 67.4,12.3 67.4,14.2 C67.4,16 68.9,17.1 73.5,18.9 C78.3,20.9 80.6,22.6 80.6,27.2 C80.6,33 76.4,36.3 69.2,36.3 C66.6,36.3 62.3,35.8 59.9,34.1 L59.1,25.8 Z" />
                <path d="M92.8,12.5 L92.8,27.6 C92.8,31.2 93.5,32.6 95.5,32.6 C96.2,32.6 96.7,32.4 97.3,32.2 L97.6,33.6 C94.6,35.2 92.3,36.2 89.4,36.2 C84.8,36.2 82.3,34.4 82.3,29.5 L82.3,12.5 L79.7,12.5 L79.7,10.2 L82.5,10.2 L88.8,4.2 L92.8,4.2 L92.8,10.2 L97,10.2 L97,12.5 L92.8,12.5 Z" />
                <path d="M123,13.1 C120.6,6.8 118.4,4 115.1,4 C113,4 110.9,4.9 110.9,8.1 C110.9,10.7 113.7,12.8 117.1,14.2 C124.5,17.4 127.1,19.4 127.1,25.4 C127.1,31.2 123.5,36.3 113.5,36.3 C110.4,36.3 105.8,35.4 102.9,33.5 L102.2,23.8 L104.1,23.8 C107.1,30.3 109.7,34.3 113.9,34.3 C116.4,34.3 118,32.9 118,30.3 C118,28.3 117.5,26.2 112.5,24 C104.2,20.4 102.4,17.5 102.4,12.4 C102.4,5.9 107,2 115,2 C118.7,2 122.2,2.7 124.9,3.9 L124.9,13 L123,13 L123,13.1 Z" />
                <path d="M139.2,22.8 C139.2,28.1 141,31.9 145.1,31.9 C148.5,31.9 150.3,31.3 151.9,27.8 L153.2,28.5 C151.3,34.6 146.7,36.2 141.4,36.2 C132.5,36.2 128.2,32.4 128.2,23 C128.2,14.2 133,9.5 141.6,9.5 C153.6,9.5 153.7,17.6 153.7,20.2 C153.7,21.9 153.2,22.8 151.5,22.8 L139.2,22.8 Z M142.2,20.6 C143.5,20.5 143.9,20.3 143.9,18.3 C143.9,16.6 143.9,11.3 141.6,11.3 C139.6,11.3 139.1,16.4 139.1,20.7 L142.2,20.6 Z" />
                <path d="M152.9,35.5 L152.9,33.9 C155.5,33.7 155.8,33.6 155.8,31.1 L155.8,5 C155.8,3.6 155.5,3 154.7,2.9 L153.3,2.7 L153.3,1.1 L166,0 L166.2,0.2 L166.2,31.1 C166.2,33.7 166.5,33.8 169.1,33.9 L169.1,35.5 L152.9,35.5 Z" />
                <path d="M168.3,10.2 L171.1,10.2 L171.1,9.6 C171.1,6.1 172.5,3.3 175,1.7 C176.7,0.6 178.9,-6.66133815e-16 181.2,-6.66133815e-16 C186.1,-6.66133815e-16 187.6,1.9 187.6,3.3 C187.6,5.4 185.3,6.5 184.4,6.5 C183.2,6.5 182.8,6.3 182.3,5 C181.7,3.2 181.3,1.9 180.5,1.9 C180.1,1.9 179.9,2.4 179.9,2.8 C179.9,5.8 181.6,8.2 181.6,10.2 L185.6,10.2 L185.6,12.5 L181.7,12.5 L181.7,31.2 C181.7,33.8 182,33.9 185.1,34 L185.1,35.6 L168.3,35.6 L168.3,34 C170.9,33.8 171.1,33.7 171.1,31.2 L171.1,12.5 L168.3,12.5 L168.3,10.2 Z" />
              </g>
              <g transform="translate(191.000000, 0.000000)">
                <polygon points="0.4 2.2 1.8 2.2 1.8 5.8 3.2 5.8 3.2 2.2 4.6 2.2 4.6 0.8 0.4 0.8" />
                <polygon points="9.5 0.8 8.4 3.7 7.2 0.8 5.6 0.8 5.6 5.8 6.9 5.8 6.9 3.5 7.9 5.8 8.9 5.8 9.9 3.4 9.9 5.8 11.2 5.8 11.2 0.8" />
              </g>
            </svg>
          </motion.div>
        </div>
      </header>

      <canvas ref={canvasRef} className="absolute inset-0 z-0"></canvas>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-dark/50 z-10"></div>
      
      <div className="container mx-auto px-6 relative z-20 text-center py-20 flex-grow flex flex-col justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center mb-6 bg-white/5 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10 mx-auto"
        >
          <Sparkles className="w-5 h-5 mr-2 text-primary" />
          <span className="text-sm font-medium tracking-wide text-white/80">Coming June 2025 • 500 Founding Members Only</span>
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight"
        >
          Reclaim Your <span className="text-primary">Attention</span>
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-sans text-xl md:text-2xl font-light text-white/70 mb-8 max-w-2xl mx-auto"
        >
          Something transformative is coming
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="max-w-2xl mx-auto mb-16"
        >
          <EmailOptIn />
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="w-16 h-16 mx-auto relative"
        >
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping"></div>
          <div className="absolute inset-3 rounded-full bg-primary/40"></div>
          <div className="absolute inset-5 rounded-full bg-primary/80"></div>
          <div className="absolute inset-7 rounded-full bg-primary"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;